import React, {useCallback, useRef, useState} from 'react';
import {Popover} from 'antd';

interface ImgProps extends React.ImgHTMLAttributes<HTMLImageElement> {
  src: string;
  previewSize?: {
    width?: number;
    height?: number;
  } | 'original';
  showPreview?: boolean;
}

const Img: React.FC<ImgProps> = ({ 
  src, 
  previewSize = { width: 400, height: 300 },
  showPreview = true,
  ...imgProps 
}) => {
  const [popoverVisible, setPopoverVisible] = useState(false);
  const [imageNaturalSize, setImageNaturalSize] = useState<{width: number, height: number} | null>(null);
  const hoverTimerRef = useRef<NodeJS.Timeout>();

  // 检测是否为触摸设备
  const isTouchDevice = useCallback(() => {
    return 'ontouchstart' in window || navigator.maxTouchPoints > 0;
  }, []);

  // 获取去掉参数的原始URL用于预览
  const getPreviewSrc = useCallback(() => {
    try {
      const url = new URL(src);
      return url.origin + url.pathname; // 只保留协议、域名和路径，去掉查询参数
    } catch {
      // 如果URL解析失败，尝试简单的字符串处理
      return src.split('?')[0];
    }
  }, [src]);

  const previewSrc = getPreviewSrc();

  // 预先加载图片获取尺寸（在后台进行，避免显示时跳动）
  const preloadImageSize = useCallback(() => {
    if (previewSize === 'original' && !imageNaturalSize) {
      const img = new Image();
      img.onload = () => {
        setImageNaturalSize({
          width: img.naturalWidth,
          height: img.naturalHeight
        });
      };
      img.src = previewSrc;
    }
  }, [previewSize, imageNaturalSize, previewSrc]);

  const handleMouseEnter = useCallback(() => {
    // 如果不需要显示预览，直接返回
    if (!showPreview) {
      return;
    }
    
    // 如果是触摸设备，不显示预览
    if (isTouchDevice()) {
      return;
    }
    
    // 预先加载图片获取尺寸
    preloadImageSize();
    
    // 清除之前的定时器
    if (hoverTimerRef.current) {
      clearTimeout(hoverTimerRef.current);
    }
    
    // 设置1秒延时显示预览
    hoverTimerRef.current = setTimeout(() => {
      setPopoverVisible(true);
    }, 1000);
  }, [showPreview, preloadImageSize, isTouchDevice]);

  const handleMouseLeave = useCallback(() => {
    // 如果不需要显示预览，直接返回
    if (!showPreview) {
      return;
    }
    
    // 如果是触摸设备，不处理
    if (isTouchDevice()) {
      return;
    }
    
    // 清除定时器
    if (hoverTimerRef.current) {
      clearTimeout(hoverTimerRef.current);
    }
    
    // 隐藏预览
    setPopoverVisible(false);
  }, [showPreview, isTouchDevice]);

  // 处理图片加载完成，获取原始尺寸
  const handlePreviewImageLoad = useCallback((e: React.SyntheticEvent<HTMLImageElement>) => {
    const img = e.target as HTMLImageElement;
    setImageNaturalSize({
      width: img.naturalWidth,
      height: img.naturalHeight
    });
  }, []);

  // 计算预览尺寸
  const getPreviewStyle = () => {
    if (previewSize === 'original') {
      // 如果还没有获取到图片尺寸，先使用默认尺寸避免跳动
      if (!imageNaturalSize) {
        return { width: 300, height: 300 };
      }
      
      // 使用原始尺寸，但限制最大尺寸为300x300，等比例缩放
      const maxSize = 300;
      const scaleX = maxSize / imageNaturalSize.width;
      const scaleY = maxSize / imageNaturalSize.height;
      const scale = Math.min(scaleX, scaleY, 1); // 不放大，只缩小
      
      return {
        width: Math.floor(imageNaturalSize.width * scale),
        height: Math.floor(imageNaturalSize.height * scale)
      };
    } else if (typeof previewSize === 'object') {
      return previewSize;
    } else {
      return { width: 400, height: 300 };
    }
  };

  const previewStyle = getPreviewStyle();

  // 预览内容
  const previewContent = (
    <div style={{ 
      width: previewStyle.width, 
      height: previewStyle.height
    }}>
      <img
        src={previewSrc}
        alt="预览图片"
        onLoad={handlePreviewImageLoad}
        style={{
          width: '100%',
          height: '100%',
          objectFit: 'contain',
          display: 'block'
        }}
        onError={(e) => {
          // 预览图片加载失败时的处理
          const target = e.target as HTMLImageElement;
          target.style.display = 'none';
        }}
      />
    </div>
  );

  return showPreview ? (
    <Popover
      content={previewContent}
      trigger={[]}
      open={popoverVisible}
      placement="rightTop"
      overlayStyle={{ maxWidth: 'none' }}
    >
      <img
        {...imgProps}
        src={src}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        style={{
          cursor: 'pointer',
          ...imgProps.style
        }}
      />
    </Popover>
  ) : (
    <img
      {...imgProps}
      src={src}
      style={{
        ...imgProps.style
      }}
    />
  );
};

export default Img;